import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../stores/authStore';
import { Verify2FADialog } from '../../components/auth/Verify2FADialog';

interface LoginFormData {
  email: string;
  password: string;
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, error: authError, isLoading, requires2FA, setRequires2FA } = useAuthStore();
  const [localError, setLocalError] = useState('');
  const [loginCredentials, setLoginCredentials] = useState<{ email: string; password: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setLocalError('');
    setLoginCredentials(data); // Store credentials for 2FA verification

    try {
      const success = await login(data);

      if (success) {
        // Redirect to home or previous page
        navigate('/');
      }
      // If requires2FA is true, the dialog will show automatically
    } catch (err: any) {
      setLocalError(err.message || 'Login failed. Please try again.');
    }
  };

  const handle2FAVerify = async (code: string) => {
    if (!loginCredentials) return;

    try {
      // Call login with 2FA code
      const success = await login({
        email: loginCredentials.email,
        password: loginCredentials.password,
        twoFactorCode: code,
      });

      if (success) {
        setRequires2FA(false);
        setLoginCredentials(null);
        navigate('/');
      }
    } catch (err: any) {
      throw err; // Let the dialog handle the error
    }
  };

  const handle2FAClose = () => {
    setRequires2FA(false);
    setLoginCredentials(null);
  };

  const displayError = localError || authError;

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" textAlign="center">
          Customer Login
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
          Log in to view your reservations and order history
        </Typography>

        {displayError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {displayError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            label="Email"
            type="email"
            fullWidth
            error={!!errors.email}
            helperText={errors.email?.message}
            autoFocus
          />

          <TextField
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters',
              },
            })}
            label="Password"
            type="password"
            fullWidth
            error={!!errors.password}
            helperText={errors.password?.message}
          />

          <Box sx={{ textAlign: 'right', mt: -1 }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Forgot password?
            </Link>
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={isLoading}
            sx={{ mt: 2 }}
          >
            {isLoading ? 'Logging in...' : 'Log In'}
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              Don't have an account?{' '}
              <Link component={RouterLink} to="/register">
                Sign up
              </Link>
            </Typography>
          </Box>

          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Link component={RouterLink} to="/" variant="body2">
              Continue as guest
            </Link>
          </Box>
        </Box>
      </Paper>

      {/* 2FA Verification Dialog */}
      <Verify2FADialog
        open={requires2FA}
        onClose={handle2FAClose}
        onVerify={handle2FAVerify}
      />
    </Container>
  );
};
